import React, { useEffect, useState } from 'react'
import { Popup } from 'antd-mobile-v5'
import { FormattedMessage, useIntl } from 'react-intl'

import styles from './EcardCouponPicker.module.css'

// 折扣率 → 中文折扣标签（0.850 → 85折）：Math.round 消除浮点精度误差。
// rate=0 为 club 0元兑换券（CLUB_FREE_PHYSICAL）：非折扣券，渲染「0元」而非「0折」
const toRateLabel = (discountRate) =>
  Number(discountRate) === 0 ? '0元' : `${Math.round(Number(discountRate) * 100)}折`

// 整卡可点 + 键盘可达（Enter/Space 触发），对齐 CartFooter 结算按钮的无障碍处理
const handleKeyPress = (onClick) => (event) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    onClick()
  }
}

/**
 * 结算弹窗内嵌的本地优惠券单选器（实体卡85折券）。
 * <p>
 * 结算弹窗空间有限，券多时全量平铺会把弹窗撑出屏幕（v5 Popup body 无 max-height）：
 * 弹窗内只渲染一行当前选中态，点开后二级弹层全量选券（券列表限高内部滚动）。
 * 选中态由父组件持有（selectedId），「不使用优惠券」= selectedId 为 null。
 * 折扣金额的权威计算在服务端（响应 couponPrice），本组件不做客户端预估。
 */
const EcardCouponPicker = ({ coupons, parentVisible, selectedId, onSelect, unavailable, unavailableText }) => {
  const { formatMessage } = useIntl()
  const [pickerVisible, setPickerVisible] = useState(false)

  // 外层结算弹窗关闭时同步收起二级弹层，避免残留悬浮弹窗
  useEffect(() => {
    if (!parentVisible) setPickerVisible(false)
  }, [parentVisible])

  if (!coupons || coupons.length === 0) return null

  const selectedCoupon = coupons.find((coupon) => coupon.id === selectedId)

  const closePicker = () => setPickerVisible(false)
  const pick = (id) => {
    onSelect(id)
    closePicker()
  }

  const cardClassName = (selected) => styles.card + ' ' + (selected ? styles.selected : '')

  return (
    <>
      {/* 一行选中态：显示当前选择的券描述（未选则「不使用优惠券」），点击展开二级弹层；
          商品金额为 0 时（unavailable）置灰展示说明文案、不可点击、隐藏箭头 */}
      <div
        className={unavailable ? styles.selection + ' ' + styles.unavailable : styles.selection}
        role={unavailable ? undefined : 'button'}
        tabIndex={unavailable ? -1 : 0}
        aria-haspopup={unavailable ? undefined : 'dialog'}
        aria-disabled={unavailable || undefined}
        onClick={unavailable ? undefined : () => setPickerVisible(true)}
        onKeyDown={unavailable ? undefined : handleKeyPress(() => setPickerVisible(true))}
      >
        <span className={styles['selection-label']}>
          <FormattedMessage id="ecard-coupon-title" />
        </span>
        <span className={styles['selection-value']}>
          {unavailable ? (
            unavailableText
          ) : selectedCoupon ? (
            selectedCoupon.description
          ) : (
            <FormattedMessage id="ecard-coupon-not-use" />
          )}
        </span>
        {!unavailable && <span className={styles['selection-arrow']} aria-hidden="true" />}
      </div>

      {/* 二级弹层：全量券列表，限高内部滚动（unavailable 时不可打开） */}
      <Popup
        visible={pickerVisible && !unavailable}
        onMaskClick={closePicker}
        bodyStyle={{
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
          maxHeight: '70vh',
        }}
      >
        <div className={styles['picker-popup']}>
          <div className={styles['picker-popup-header']}>
            <span className={styles['picker-popup-title']}>
              <FormattedMessage id="ecard-coupon-title" />
            </span>
            <span
              className={styles['picker-popup-close']}
              role="button"
              tabIndex={0}
              aria-label={formatMessage({ id: 'common-close' })}
              onClick={closePicker}
              onKeyDown={handleKeyPress(closePicker)}
            >
              ✕
            </span>
          </div>
          <div className={styles['picker-list']}>
            <div
              className={cardClassName(selectedId == null)}
              role="button"
              tabIndex={0}
              aria-pressed={selectedId == null}
              onClick={() => pick(null)}
              onKeyDown={handleKeyPress(() => pick(null))}
            >
              <div className={styles.content}>
                <div className={styles.desc}>
                  <FormattedMessage id="ecard-coupon-not-use" />
                </div>
              </div>
            </div>
            {coupons.map((coupon) => (
              <div
                className={cardClassName(selectedId === coupon.id)}
                key={coupon.id}
                role="button"
                tabIndex={0}
                aria-pressed={selectedId === coupon.id}
                onClick={() => pick(coupon.id)}
                onKeyDown={handleKeyPress(() => pick(coupon.id))}
              >
                <div className={styles.tab}>
                  <FormattedMessage id="coupon" />
                </div>
                <div className={styles.content}>
                  <div className={styles.desc}>{coupon.description}</div>
                  <div className={styles.rate}>{toRateLabel(coupon.discountRate)}</div>
                </div>
                {coupon.expiredAt && (
                  <div className={styles.conditions}>
                    <FormattedMessage id="ecard-coupon-expiry" values={{ date: coupon.expiredAt }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </Popup>
    </>
  )
}

export default EcardCouponPicker
